import { type FormEvent, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../lib/api-client";
import { queryKeys } from "../lib/queryClient";
import { TicketStatusBadge, TicketPriorityBadge } from "../components/StatusBadge";
import { ErrorBanner, LoadingState, describeError } from "../components/States";
import { formatDateTime } from "../lib/format";
import { usePageMeta } from "../lib/seo";

export function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [body, setBody] = useState("");

  const { data: ticket, isLoading, isError, error, refetch } = useQuery({
    queryKey: queryKeys.ticket(id ?? ""),
    queryFn: () => apiClient.getTicket(id as string),
    enabled: Boolean(id),
    // Short poll while the tab is open, same reasoning as the buyer/seller chat thread — cheap
    // way to see a staff reply land without a manual refresh, no websocket infra needed.
    refetchInterval: 8_000,
  });

  usePageMeta({
    title: ticket ? `Ticket: ${ticket.subject}` : "Ticket",
    description: "Support ticket detail.",
    path: `/tickets/${id ?? ""}`,
    index: false,
  });

  const reply = useMutation({
    mutationFn: (text: string) => apiClient.addTicketMessage(id as string, { body: text }),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.ticket(id ?? ""), updated);
      setBody("");
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = body.trim();
    if (!trimmed) return;
    reply.mutate(trimmed);
  }

  if (isLoading) return <LoadingState label="Loading ticket…" />;
  if (isError) return <ErrorBanner error={error} onRetry={() => void refetch()} />;
  if (!ticket) return null;

  const isCancelled = ticket.status === "CANCELLED";

  return (
    <div className="stack" style={{ maxWidth: 680 }}>
      <div className="row row--between">
        <h1>{ticket.subject}</h1>
        <div className="row">
          <TicketStatusBadge status={ticket.status} />
          <TicketPriorityBadge priority={ticket.priority} />
        </div>
      </div>

      <div className="card">
        <div className="row row--between">
          <span className="muted">Opened</span>
          <span>{formatDateTime(ticket.createdAt)}</span>
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="message-thread">
          {ticket.messages.map((m) => (
            <div key={m.id} className={`message-bubble ${!m.isFromAdmin ? "is-mine" : ""}`}>
              <p className="text-sm muted">
                <strong>{m.authorName}</strong> · {formatDateTime(m.createdAt)}
              </p>
              <p className="text-sm">{m.body}</p>
            </div>
          ))}
        </div>

        {isCancelled ? (
          <p
            className="text-sm muted"
            style={{ padding: "var(--space-3) var(--space-4)", borderTop: "1px solid var(--color-border)" }}
          >
            This ticket is cancelled and can&apos;t accept new replies —{" "}
            <Link to="/tickets/new">open a new ticket</Link> if you still need help.
          </p>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="row"
            style={{ padding: "var(--space-3) var(--space-4)", borderTop: "1px solid var(--color-border)" }}
          >
            <input
              type="text"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write a reply…"
              maxLength={5_000}
              style={{ flex: 1 }}
            />
            <button type="submit" className="btn btn-primary" disabled={!body.trim() || reply.isPending}>
              {reply.isPending ? "Sending…" : "Send"}
            </button>
          </form>
        )}
        {reply.isError && (
          <p
            className="field-error-text"
            role="alert"
            style={{ padding: "0 var(--space-4) var(--space-3)" }}
          >
            {describeError(reply.error)}
          </p>
        )}
      </div>
    </div>
  );
}
